import type { Permission } from '@makila/shared';
import {
  ArrowLeftRight, Banknote, BarChart3, Boxes, Building2, CreditCard, Crown, UserCog, ClipboardList, FileText, HandCoins, Home, Landmark, LayoutGrid, type LucideIcon,
  PieChart, Receipt, ScrollText, Settings, ShieldCheck, ShoppingCart, Truck, Users, Wallet,
} from 'lucide-react';

/**
 * Navegação de barra dupla: o carril mostra os módulos; o painel mostra as
 * secções do módulo activo. Um só sítio define as duas coisas — a paleta de
 * comandos e o menu móvel leem daqui também.
 */
export interface NavSection {
  to: string;
  label: string;
  /** Sem permissão = visível a todos os membros (ex.: Segurança da própria conta). */
  perm?: Permission;
  /** Chave no objecto de contadores de /reports/nav-counts. */
  count?: (c: NavCounts) => number | undefined;
  tone?: 'warn';
}
/** Grupo com `title` = categoria recolhível no painel; sem título = itens soltos no topo. */
export interface NavGroup { title?: string; icon?: LucideIcon; items: NavSection[] }
export interface Module {
  id: string;
  label: string;
  icon: LucideIcon;
  perm: Permission;
  /** Prefixos de caminho que pertencem a este módulo. */
  match: string[];
  home: string;
  action?: { label: string; to: string; perm: Permission };
  groups: NavGroup[];
  /** Consola da plataforma: só administradores do Makila (não depende das permissões da empresa). */
  platform?: boolean;
}

export interface NavCounts {
  drafts: Record<string, number>;
  draftsTotal: number;
  overdue: number;
  lowStock: number;
  purchaseDrafts: number;
  openInventories: number;
  quotesExpiring: number;
  recurringFailed: number;
}

const d = (t: string) => (c: NavCounts) => c.drafts[t];

export const MODULES: Module[] = [
  {
    id: 'home', label: 'Início', icon: Home, perm: 'documents:view', match: ['/'], home: '/',
    groups: [{ items: [{ to: '/', label: 'Painel', perm: 'documents:view' }] }],
  },
  {
    id: 'sales', label: 'Vendas', icon: FileText, perm: 'documents:view', match: ['/vendas', '/documentos'], home: '/vendas',
    action: { label: 'Nova factura', to: '/documentos/novo?tipo=FT', perm: 'documents:create' },
    groups: [
      { items: [{ to: '/vendas', label: 'Todos os documentos', perm: 'documents:view', count: (c) => c.draftsTotal || undefined }] },
      { title: 'Facturação', icon: FileText, items: [
        { to: '/vendas?tipo=FT', label: 'Facturas', perm: 'documents:view', count: d('FT') },
        { to: '/vendas?tipo=FR', label: 'Facturas-recibo', perm: 'documents:view', count: d('FR') },
        { to: '/vendas?tipo=VD', label: 'Vendas a dinheiro', perm: 'documents:view', count: d('VD') },
        { to: '/vendas?tipo=PF', label: 'Pró-formas', perm: 'documents:view', count: d('PF') },
        { to: '/vendas/avencas', label: 'Avenças', perm: 'documents:view', count: (c) => c.recurringFailed || undefined, tone: 'warn' },
      ] },
      { title: 'Correcções', icon: ArrowLeftRight, items: [
        { to: '/vendas?tipo=NC', label: 'Notas de crédito', perm: 'documents:view', count: d('NC') },
        { to: '/vendas?tipo=ND', label: 'Notas de débito', perm: 'documents:view', count: d('ND') },
      ] },
      { title: 'Cobranças', icon: Receipt, items: [
        { to: '/vendas?tipo=RE', label: 'Recibos', perm: 'documents:view', count: d('RE') },
      ] },
      { title: 'Transporte', icon: Truck, items: [
        { to: '/vendas?tipo=GR', label: 'Guias de remessa', perm: 'documents:view', count: d('GR') },
        { to: '/vendas?tipo=GD', label: 'Guias de devolução', perm: 'documents:view', count: d('GD') },
      ] },
    ],
  },
  {
    id: 'customers', label: 'Clientes', icon: Users, perm: 'customers:view', match: ['/clientes', '/contas-correntes'], home: '/clientes',
    action: { label: 'Novo cliente', to: '/clientes?novo=1', perm: 'customers:create' },
    groups: [
      { title: 'Carteira', icon: Users, items: [{ to: '/clientes', label: 'Clientes', perm: 'customers:view' }] },
      { title: 'Cobranças', icon: Wallet, items: [
        { to: '/contas-correntes', label: 'Contas correntes', perm: 'accounts:view', count: (c) => c.overdue || undefined, tone: 'warn' },
      ] },
    ],
  },
  {
    id: 'cash', label: 'Tesouraria', icon: Banknote, perm: 'cash:view', match: ['/caixa'], home: '/caixa',
    groups: [{ title: 'Caixa e bancos', icon: Banknote, items: [{ to: '/caixa', label: 'Caixa e bancos', perm: 'cash:view' }] }],
  },
  {
    id: 'purchases', label: 'Compras', icon: ShoppingCart, perm: 'purchases:view', match: ['/compras', '/fornecedores'], home: '/compras',
    action: { label: 'Registar factura', to: '/compras/nova', perm: 'purchases:create' },
    groups: [
      { title: 'Documentos', icon: FileText, items: [
        { to: '/compras', label: 'Facturas de fornecedor', perm: 'purchases:view', count: (c) => c.purchaseDrafts || undefined },
        { to: '/compras/pagamentos', label: 'Pagamentos', perm: 'purchases:view' },
      ] },
      { title: 'Tesouraria', icon: HandCoins, items: [{ to: '/compras/contas-a-pagar', label: 'Contas a pagar', perm: 'purchases:view' }] },
      { title: 'Cadastro', icon: Truck, items: [{ to: '/fornecedores', label: 'Fornecedores', perm: 'purchases:view' }] },
    ],
  },
  {
    id: 'stock', label: 'Stock', icon: Boxes, perm: 'products:view', match: ['/artigos', '/stock'], home: '/artigos',
    action: { label: 'Novo artigo', to: '/artigos?novo=1', perm: 'products:create' },
    groups: [
      { title: 'Catálogo', icon: LayoutGrid, items: [
        { to: '/artigos', label: 'Artigos', perm: 'products:view' },
        { to: '/stock/categorias', label: 'Categorias', perm: 'products:view' },
      ] },
      { title: 'Existências', icon: Boxes, items: [
        { to: '/stock', label: 'Existências', perm: 'stock:view', count: (c) => c.lowStock || undefined, tone: 'warn' },
        { to: '/stock/movimentos', label: 'Movimentos', perm: 'stock:view' },
      ] },
      { title: 'Controlo', icon: ClipboardList, items: [
        { to: '/stock/inventarios', label: 'Inventários', perm: 'stock:view', count: (c) => c.openInventories || undefined },
        { to: '/stock/armazens', label: 'Armazéns', perm: 'stock:view' },
      ] },
    ],
  },
  {
    id: 'reports', label: 'Relatórios', icon: BarChart3, perm: 'reports:view', match: ['/relatorios'], home: '/relatorios',
    groups: [
      { title: 'Vendas', icon: BarChart3, items: [
        { to: '/relatorios?r=periodo', label: 'Por período', perm: 'reports:view' },
        { to: '/relatorios?r=clientes', label: 'Por cliente', perm: 'reports:view' },
        { to: '/relatorios?r=artigos', label: 'Por artigo', perm: 'reports:view' },
      ] },
      { title: 'Fiscal e tesouraria', icon: PieChart, items: [
        { to: '/relatorios?r=iva', label: 'Mapa de IVA', perm: 'reports:view' },
        { to: '/relatorios?r=recebimentos', label: 'Recebimentos', perm: 'reports:view' },
      ] },
    ],
  },
  {
    id: 'fiscal', label: 'Fiscal', icon: Landmark, perm: 'exports:view', match: ['/comunicacao-at', '/fiscal', '/auditoria'], home: '/comunicacao-at',
    groups: [
      { title: 'Autoridade Tributária', icon: Landmark, items: [
        { to: '/comunicacao-at', label: 'Comunicação AT', perm: 'exports:view' },
        { to: '/fiscal/series', label: 'Séries', perm: 'settings:view' },
      ] },
      { title: 'Controlo interno', icon: ScrollText, items: [{ to: '/auditoria', label: 'Auditoria', perm: 'audit:view' }] },
    ],
  },
  {
    id: 'settings', label: 'Definições', icon: Settings, perm: 'settings:view', match: ['/definicoes', '/subscricao'], home: '/definicoes',
    groups: [
      { title: 'Organização', icon: Building2, items: [
        { to: '/definicoes?tab=empresa', label: 'Empresa', perm: 'settings:view' },
        { to: '/definicoes?tab=documentos', label: 'Documentos', perm: 'settings:view' },
        { to: '/definicoes?tab=bancos', label: 'Contas bancárias', perm: 'settings:view' },
        { to: '/definicoes?tab=automacao', label: 'Automação', perm: 'settings:view' },
        { to: '/subscricao', label: 'Subscrição e pagamento', perm: 'billing:view' },
      ] },
      { title: 'Acessos', icon: ShieldCheck, items: [
        { to: '/definicoes?tab=equipa', label: 'Equipa', perm: 'users:view' },
        { to: '/definicoes?tab=seguranca', label: 'Perfil e segurança' },
      ] },
    ],
  },
  {
    id: 'platform', label: 'Makila', icon: Crown, perm: 'documents:view', platform: true, match: ['/plataforma'], home: '/plataforma',
    groups: [
      { items: [{ to: '/plataforma', label: 'Visão geral' }] },
      { title: 'Clientes do SaaS', icon: Building2, items: [
        { to: '/plataforma/empresas', label: 'Empresas' },
        { to: '/plataforma/empresas?estado=active', label: 'Activas' },
        { to: '/plataforma/empresas?estado=trial', label: 'Em teste' },
        { to: '/plataforma/empresas?estado=past_due', label: 'Pagamento em atraso' },
        { to: '/plataforma/empresas?estado=read_only', label: 'Só leitura' },
        { to: '/plataforma/empresas?estado=suspended', label: 'Suspensas' },
        { to: '/plataforma/empresas?estado=closed', label: 'Encerradas' },
      ] },
      { title: 'Facturação do SaaS', icon: CreditCard, items: [
        { to: '/plataforma/pagamentos', label: 'Pagamentos' },
        { to: '/plataforma/cobrancas', label: 'Pedidos de pagamento' },
        { to: '/plataforma/gateways', label: 'Gateways de pagamento' },
        { to: '/plataforma/codigos-promocionais', label: 'Códigos promocionais' },
        { to: '/plataforma/parceiros', label: 'Parceiros' },
        { to: '/plataforma/planos', label: 'Planos' },
      ] },
      { title: 'Administração', icon: UserCog, items: [
        { to: '/plataforma/utilizadores', label: 'Utilizadores' },
        { to: '/plataforma/avisos', label: 'Avisos' },
        { to: '/plataforma/sistema', label: 'Sistema' },
        { to: '/plataforma/auditoria', label: 'Auditoria' },
      ] },
    ],
  },
];

export function moduleFor(pathname: string): Module {
  if (pathname === '/') return MODULES[0]!;
  return MODULES.slice(1).find((m) => m.match.some((p) => pathname === p || pathname.startsWith(p + '/'))) ?? MODULES[0]!;
}
